<script setup>
import { computed, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useScheduledMessages } from 'dashboard/composables/useScheduledMessages';

import Icon from 'next/icon/Icon.vue';
import ScheduledMessageBubble from './ScheduledMessageBubble.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  // The thread's messages, as the store holds them (snake_case).
  messages: { type: Array, default: () => [] },
});

const emit = defineEmits(['loaded']);

const { t } = useI18n();

const { scheduledMessages, fetchScheduledMessages, cancelScheduledMessage } =
  useScheduledMessages(toRef(props, 'conversationId'));

// The job marks the message it sends with the schedule's id. Once that message is in the thread,
// the placeholder is the same message twice, so it goes as soon as the real one arrives.
const sentScheduleIds = computed(
  () =>
    new Set(
      props.messages
        .map(message => message.content_attributes?.scheduled_message_id)
        .filter(Boolean)
    )
);

const pendingMessages = computed(() =>
  scheduledMessages.value.filter(
    scheduled => !sentScheduleIds.value.has(scheduled.id)
  )
);

watch(
  () => props.conversationId,
  async () => {
    await fetchScheduledMessages();
    if (pendingMessages.value.length) emit('loaded');
  },
  { immediate: true }
);
</script>

<template>
  <template v-if="pendingMessages.length">
    <li class="flex justify-center items-center py-3 list-none">
      <span
        class="flex gap-1 items-center px-2.5 py-1 text-xs font-medium rounded-full bg-n-alpha-1 text-n-slate-11"
      >
        <Icon icon="i-lucide-clock" class="size-3" />
        {{ t('SCHEDULED_MESSAGES.THREAD.DIVIDER') }}
      </span>
    </li>
    <ScheduledMessageBubble
      v-for="scheduled in pendingMessages"
      :key="scheduled.id"
      :scheduled-message="scheduled"
      @cancel="cancelScheduledMessage(scheduled.id)"
    />
  </template>
</template>
